import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { getUnreadCount, getTask, getSeguimientoStatus } from '../utils/api';
import NotificationPanel from './NotificationPanel';
import TaskDrawer from './TaskDrawer';
import AvatarMenu from './AvatarMenu';
import InstallPrompt from './InstallPrompt';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(location.pathname.startsWith('/admin'));
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [users] = useState([]);

  useEffect(() => {
    loadUnreadCount();
    loadPendingUpdates();
    const interval = setInterval(() => {
      loadUnreadCount();
      loadPendingUpdates();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const res = await getUnreadCount();
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const loadPendingUpdates = async () => {
    try {
      const res = await getSeguimientoStatus();
      const pending = (res.data.items || []).filter(i => !i.has_update).length;
      setPendingUpdates(pending);
    } catch {}
  };

  const handleOpenTaskFromNotification = async (taskId) => {
    try {
      const taskRes = await getTask(taskId);
      setSelectedTask(taskRes.data);
    } catch {}
  };

  const handleLogout = async () => {
    try {
      logout();
      await new Promise(resolve => setTimeout(resolve, 100));
      window.location.href = '/login';
    } catch {
      window.location.href = '/login';
    }
  };

  const allNavItems = [
    { path: '/dashboard',  label: 'Dashboard',    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/mi-semana',  label: 'Mi Semana',    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', badge: pendingUpdates },
    { path: '/tasks',      label: 'Tareas',        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
    { path: '/kanban',     label: 'Kanban',        icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2z' },
    { path: '/calendar',   label: 'Calendario',    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  ];

  if (user?.role === 'Presidencia' || user?.role === 'Gerencia') {
    allNavItems.splice(2, 0, {
      path: '/seguimiento',
      label: 'Seguimiento',
      icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    });
  }

  if (user?.role === 'Presidencia') {
    allNavItems.push({
      path: '/admin',
      label: 'Administración',
      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      adminOnly: true,
    });
  }

  // Bottom nav: up to 4 items + "Más" drawer trigger
  const bottomNav = allNavItems.slice(0, 4);
  const hasMore = allNavItems.length > 4;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // ─── Sidebar item (desktop) ────────────────────────────────────────────────
  const SidebarItem = ({ item }) => {
    if (item.adminOnly) {
      return (
        <div>
          <button
            onClick={() => setAdminExpanded(!adminExpanded)}
            className={`sidebar-item w-full ${location.pathname.startsWith('/admin') ? 'sidebar-item-active' : ''}`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
            </svg>
            <span className="font-semibold text-sm flex-1 text-left">{item.label}</span>
            <svg className={`w-4 h-4 transition-transform ${adminExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {adminExpanded && (
            <div className="ml-7 mt-0.5 space-y-0.5">
              {[{ path: '/admin/users', label: 'Usuarios' }, { path: '/admin/tags', label: 'Tags' }].map(sub => (
                <button key={sub.path} onClick={() => navigate(sub.path)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === sub.path ? 'text-primary bg-primary/8' : 'hover:bg-white/[0.04] hover:text-white'}`}
                  style={{ color: location.pathname === sub.path ? '#00C878' : 'hsl(var(--sidebar-text))' }}>
                  {sub.label}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }
    return (
      <button onClick={() => navigate(item.path)}
        className={`sidebar-item w-full ${isActive(item.path) ? 'sidebar-item-active' : ''}`}>
        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={item.icon} />
        </svg>
        <span className="text-sm flex-1 text-left">{item.label}</span>
        {item.badge > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background">

      {/* ── Desktop Sidebar ─────────────────────────────────────────────────── */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 z-30"
        style={{ background: 'linear-gradient(180deg, #0F1215 0%, #161A20 100%)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="border-b border-white/[0.07] flex items-center justify-center overflow-hidden" style={{ height: '120px' }}>
          <img src="/nexia-logo.png" alt="Nexia" style={{ height: '160px', width: 'auto', marginTop: '-12px' }} />
        </div>
        <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto">
          {allNavItems.map(item => <SidebarItem key={item.path} item={item} />)}
        </nav>
        <div className="p-4 border-t border-white/[0.07]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-xs" style={{ color: 'hsl(var(--sidebar-text))' }}>{user?.role}</div>
            </div>
            <button onClick={() => setNotificationPanelOpen(true)}
              className="relative flex-shrink-0 p-1.5" style={{ color: 'hsl(var(--sidebar-text))' }}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 bg-destructive text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
          <button onClick={handleLogout}
            className="w-full px-4 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
            style={{ color: 'hsl(var(--sidebar-text))' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = '#EF4444'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'hsl(var(--sidebar-text))'; }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* ── Mobile Top Bar ───────────────────────────────────────────────────── */}
      <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4"
        style={{ height: 56, background: 'rgba(15,18,21,0.97)', borderBottom: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)' }}>
        {/* Logo small */}
        <div style={{ height: 36, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          <img src="/nexia-logo.png" alt="Nexia" style={{ height: 52, width: 'auto', marginTop: -8 }} />
        </div>
        <div className="flex items-center gap-1">
          {/* Notifications */}
          <button onClick={() => setNotificationPanelOpen(true)}
            className="relative flex items-center justify-center rounded-xl"
            style={{ width: 44, height: 44, color: '#94A3B8' }}>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {/* Avatar + menu */}
          <button onClick={() => setSidebarOpen(true)}
            className="flex items-center justify-center rounded-xl font-bold text-white"
            style={{ width: 44, height: 44, background: 'rgba(99,102,241,0.18)', fontSize: 16 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      {/* ── Mobile Slide-out Menu ────────────────────────────────────────────── */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.6)' }}
              onClick={() => setSidebarOpen(false)} />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="md:hidden fixed right-0 top-0 bottom-0 z-50 flex flex-col"
              style={{ width: 280, background: 'linear-gradient(180deg, #0F1215 0%, #161A20 100%)', borderLeft: '1px solid rgba(255,255,255,0.07)' }}>

              {/* User info */}
              <div className="px-5 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg text-white"
                    style={{ background: 'linear-gradient(135deg,#6366F1,#818CF8)' }}>
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-white text-base">{user?.name}</p>
                    <p className="text-sm" style={{ color: '#64748B' }}>{user?.role}</p>
                  </div>
                </div>
              </div>

              {/* All nav */}
              <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {allNavItems.map(item => (
                  <button key={item.path}
                    onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 rounded-xl px-4 transition-colors"
                    style={{
                      height: 52,
                      background: isActive(item.path) ? 'rgba(0,200,120,0.1)' : 'transparent',
                      color: isActive(item.path) ? '#00C878' : '#94A3B8',
                    }}>
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    <span className="font-semibold text-base">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B' }}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>

              {/* Create task + logout */}
              <div className="p-4 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button onClick={() => { navigate('/tasks/create'); setSidebarOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 rounded-xl font-bold text-black"
                  style={{ height: 48, background: '#00C878', fontSize: 15 }}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  Nueva Tarea
                </button>
                <button onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 rounded-xl font-semibold"
                  style={{ height: 48, background: 'rgba(239,68,68,0.08)', color: '#EF4444', fontSize: 15 }}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Cerrar Sesión
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Mobile Bottom Navigation ─────────────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex items-stretch"
        style={{ height: 64, background: 'rgba(10,13,18,0.97)', borderTop: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)' }}>
        {bottomNav.map(item => {
          const active = isActive(item.path);
          return (
            <button key={item.path} onClick={() => navigate(item.path)}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 relative"
              style={{ color: active ? '#00C878' : '#475569' }}>
              <div className="relative">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2 : 1.75} d={item.icon} />
                </svg>
                {item.badge > 0 && (
                  <span className="absolute -top-1 -right-1.5 text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center leading-none"
                    style={{ background: '#F59E0B' }}>
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold leading-none">{item.label}</span>
              {active && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full"
                  style={{ width: 32, height: 2, background: '#00C878' }} />
              )}
            </button>
          );
        })}

        {/* Botón "+" central para crear tarea */}
        <button onClick={() => navigate('/tasks/create')}
          className="flex-1 flex flex-col items-center justify-center gap-0.5">
          <div className="flex items-center justify-center rounded-full"
            style={{ width: 44, height: 44, background: 'linear-gradient(135deg,#00C878,#00FFB3)', marginTop: -20, boxShadow: '0 4px 16px rgba(0,200,120,0.4)' }}>
            <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <span className="text-[10px] font-semibold leading-none" style={{ color: '#00C878', marginTop: -6 }}>Nueva</span>
        </button>

        {hasMore && (
          <button onClick={() => setSidebarOpen(true)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5"
            style={{ color: '#475569' }}>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            <span className="text-[10px] font-semibold leading-none">Más</span>
          </button>
        )}
      </nav>

      {/* ── Main Content ────────────────────────────────────────────────────── */}
      {/* pb-16 on mobile to clear the bottom nav */}
      <main className="md:ml-64 pb-20 md:pb-0">
        {children}
      </main>

      <NotificationPanel
        isOpen={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
        onOpenTask={handleOpenTaskFromNotification}
      />

      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onUpdate={() => {}}
        />
      )}

      <InstallPrompt />
    </div>
  );
};

export default Layout;
